import {
    type RefObject,
    useCallback,
    useEffect,
    useState
} from "react";
import {
    type LayoutChangeEvent,
    useWindowDimensions,
    type HostInstance,
    Platform
} from "react-native";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    DirectionType
} from "../../types/locale";

export type OverlaySideType = "bottom" | "right" | "left" | "top";

export type OverlayAlignType = "center" | "start" | "end";

export type AnchoredRectType = {
    height: number;
    width: number;
    x: number;
    y: number;
};

export type AnchoredSizeType = {
    height: number;
    width: number;
};

export type AnchoredPositionConstantType = {
    boundaryRect: AnchoredRectType;
    contentSize: AnchoredSizeType;
    anchorRect: AnchoredRectType;
    collisionPadding: number;
    direction: DirectionType;
    align: OverlayAlignType;
    side: OverlaySideType;
    offset: number;
};

export type AnchoredPositionType = {
    actualAlign: OverlayAlignType;
    actualSide: OverlaySideType;
    availableHeight: number;
    availableWidth: number;
    arrowOffset: number;
    left: number;
    top: number;
};

export type AnchoredPositionHookConstantType = {
    customTheme?: {
        gapPropagation?: keyof NCoreUIKit.GapPropagationKey;
        sharpness?: keyof NCoreUIKit.SharpnessKey;
        paletteKey?: keyof NCoreUIKit.PaletteKey;
        themeKey?: keyof NCoreUIKit.ThemeKey;
    };
    anchorRef: RefObject<HostInstance | null>;
    contentSize?: AnchoredSizeType;
    collisionPadding?: number;
    align?: OverlayAlignType;
    side?: OverlaySideType;
    isActive?: boolean;
    offset?: number;
};

export type AnchoredPositionHookType = AnchoredPositionType & {
    onContentLayout: (event: LayoutChangeEvent) => void;
    isPositioned: boolean;
    measure: () => void;
};

type AxisType = {
    start: number;
    size: number;
};

type SideSpaceConstantType = {
    boundaryRect: AnchoredRectType;
    anchorRect: AnchoredRectType;
    collisionPadding: number;
    side: OverlaySideType;
    offset: number;
};

type SideStartConstantType = {
    contentSize: AnchoredSizeType;
    anchorRect: AnchoredRectType;
    side: OverlaySideType;
    offset: number;
};

type AlignStartConstantType = {
    align: OverlayAlignType;
    isMirrored: boolean;
    contentSize: number;
    anchor: AxisType;
};

type ClampedStartConstantType = {
    isEndPreferred: boolean;
    boundary: AxisType;
    padding: number;
    start: number;
    size: number;
};

const OPPOSITE_SIDES: Record<OverlaySideType, OverlaySideType> = {
    bottom: "top",
    right: "left",
    left: "right",
    top: "bottom"
};

const OPPOSITE_ALIGNS: Record<OverlayAlignType, OverlayAlignType> = {
    center: "center",
    start: "end",
    end: "start"
};

const EMPTY_RECT: AnchoredRectType = {
    height: 0,
    width: 0,
    x: 0,
    y: 0
};

const isVerticalSide = (side: OverlaySideType) => side === "bottom" || side === "top";

const getSideSpace = ({
    collisionPadding,
    boundaryRect,
    anchorRect,
    offset,
    side
}: SideSpaceConstantType) => {
    if(side === "bottom") {
        return boundaryRect.y + boundaryRect.height - collisionPadding - anchorRect.y - anchorRect.height - offset;
    }

    if(side === "top") {
        return anchorRect.y - offset - boundaryRect.y - collisionPadding;
    }

    if(side === "right") {
        return boundaryRect.x + boundaryRect.width - collisionPadding - anchorRect.x - anchorRect.width - offset;
    }

    return anchorRect.x - offset - boundaryRect.x - collisionPadding;
};

const getSideStart = ({
    contentSize,
    anchorRect,
    offset,
    side
}: SideStartConstantType) => {
    if(side === "bottom") {
        return anchorRect.y + anchorRect.height + offset;
    }

    if(side === "top") {
        return anchorRect.y - offset - contentSize.height;
    }

    if(side === "right") {
        return anchorRect.x + anchorRect.width + offset;
    }

    return anchorRect.x - offset - contentSize.width;
};

const getAlignStart = ({
    contentSize,
    isMirrored,
    anchor,
    align
}: AlignStartConstantType) => {
    if(align === "center") {
        return anchor.start + (anchor.size - contentSize) / 2;
    }

    if((align === "start") !== isMirrored) {
        return anchor.start;
    }

    return anchor.start + anchor.size - contentSize;
};

const getClampedStart = ({
    isEndPreferred,
    boundary,
    padding,
    start,
    size
}: ClampedStartConstantType) => {
    const minStart = boundary.start + padding;

    const maxStart = boundary.start + boundary.size - padding - size;

    if(maxStart < minStart) {
        return isEndPreferred ? maxStart : minStart;
    }

    return Math.min(Math.max(start, minStart), maxStart);
};

const isInsideBoundary = ({
    boundary,
    padding,
    start,
    size
}: Omit<ClampedStartConstantType, "isEndPreferred">) => {
    return start >= boundary.start + padding && start + size <= boundary.start + boundary.size - padding;
};

export const getAnchoredPosition = ({
    collisionPadding,
    boundaryRect,
    contentSize,
    anchorRect,
    direction,
    offset,
    align,
    side
}: AnchoredPositionConstantType): AnchoredPositionType => {
    const requestedSpace = getSideSpace({
        collisionPadding,
        boundaryRect,
        anchorRect,
        offset,
        side
    });

    const oppositeSpace = getSideSpace({
        side: OPPOSITE_SIDES[side],
        collisionPadding,
        boundaryRect,
        anchorRect,
        offset
    });

    const requestedSize = isVerticalSide(side) ? contentSize.height : contentSize.width;

    const isFlipped = requestedSize > requestedSpace && (requestedSize <= oppositeSpace || oppositeSpace > requestedSpace);

    const actualSide = isFlipped ? OPPOSITE_SIDES[side] : side;

    const actualSpace = isFlipped ? oppositeSpace : requestedSpace;

    const isVertical = isVerticalSide(actualSide);

    const isMirrored = isVertical && direction === "rtl";

    const mainSize = isVertical ? contentSize.height : contentSize.width;

    const crossSize = isVertical ? contentSize.width : contentSize.height;

    const mainBoundary: AxisType = isVertical ? {
        size: boundaryRect.height,
        start: boundaryRect.y
    } : {
        size: boundaryRect.width,
        start: boundaryRect.x
    };

    const crossBoundary: AxisType = isVertical ? {
        size: boundaryRect.width,
        start: boundaryRect.x
    } : {
        size: boundaryRect.height,
        start: boundaryRect.y
    };

    const crossAnchor: AxisType = isVertical ? {
        size: anchorRect.width,
        start: anchorRect.x
    } : {
        size: anchorRect.height,
        start: anchorRect.y
    };

    const sideStart = getSideStart({
        side: actualSide,
        contentSize,
        anchorRect,
        offset
    });

    const mainStart = mainSize > actualSpace ? getClampedStart({
        isEndPreferred: !isVertical && direction === "rtl",
        padding: collisionPadding,
        boundary: mainBoundary,
        start: sideStart,
        size: mainSize
    }) : sideStart;

    const alignStart = getAlignStart({
        contentSize: crossSize,
        anchor: crossAnchor,
        isMirrored,
        align
    });

    const oppositeAlignStart = getAlignStart({
        align: OPPOSITE_ALIGNS[align],
        contentSize: crossSize,
        anchor: crossAnchor,
        isMirrored
    });

    const isAlignFlipped = align !== "center" && !isInsideBoundary({
        padding: collisionPadding,
        boundary: crossBoundary,
        start: alignStart,
        size: crossSize
    }) && isInsideBoundary({
        padding: collisionPadding,
        start: oppositeAlignStart,
        boundary: crossBoundary,
        size: crossSize
    });

    const crossStart = getClampedStart({
        start: isAlignFlipped ? oppositeAlignStart : alignStart,
        isEndPreferred: isMirrored,
        padding: collisionPadding,
        boundary: crossBoundary,
        size: crossSize
    });

    const anchorCenterOffset = crossAnchor.start + crossAnchor.size / 2 - crossStart;

    const mainAvailableSize = Math.max(actualSpace, 0);

    const crossAvailableSize = Math.max(crossBoundary.size - collisionPadding * 2, 0);

    return {
        availableHeight: isVertical ? mainAvailableSize : crossAvailableSize,
        availableWidth: isVertical ? crossAvailableSize : mainAvailableSize,
        arrowOffset: Math.min(Math.max(anchorCenterOffset, 0), crossSize),
        actualAlign: isAlignFlipped ? OPPOSITE_ALIGNS[align] : align,
        left: isVertical ? crossStart : mainStart,
        top: isVertical ? mainStart : crossStart,
        actualSide
    };
};

const useAnchoredPosition = ({
    collisionPadding: collisionPaddingProp,
    contentSize: contentSizeProp,
    offset: offsetProp,
    align = "start",
    isActive = true,
    side = "bottom",
    customTheme,
    anchorRef
}: AnchoredPositionHookConstantType): AnchoredPositionHookType => {
    const {
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const {
        bottom: insetBottom,
        right: insetRight,
        left: insetLeft,
        top: insetTop
    } = useSafeAreaInsets();

    const {
        height: windowHeight,
        width: windowWidth
    } = useWindowDimensions();

    const [
        anchorRect,
        setAnchorRect
    ] = useState<AnchoredRectType | null>(null);

    const [
        measuredContentSize,
        setMeasuredContentSize
    ] = useState<AnchoredSizeType | null>(null);

    const measure = useCallback(() => {
        const anchor = anchorRef.current;

        if(!anchor) {
            return;
        }

        const onMeasure = (rect: AnchoredRectType) => {
            setAnchorRect((previousRect) => {
                if(
                    previousRect &&
                    previousRect.height === rect.height &&
                    previousRect.width === rect.width &&
                    previousRect.x === rect.x &&
                    previousRect.y === rect.y
                ) {
                    return previousRect;
                }

                return rect;
            });
        };

        if(Platform.OS === "web" && typeof anchor.getBoundingClientRect === "function") {
            const {
                height,
                width,
                left,
                top
            } = anchor.getBoundingClientRect();

            onMeasure({
                x: left,
                y: top,
                height,
                width
            });

            return;
        }

        anchor.measureInWindow((x, y, width, height) => {
            onMeasure({
                height,
                width,
                x,
                y
            });
        });
    }, [
        anchorRef
    ]);

    useEffect(() => {
        if(!isActive) {
            setAnchorRect(null);

            return;
        }

        measure();
    }, [
        windowHeight,
        windowWidth,
        isActive,
        measure
    ]);

    useEffect(() => {
        if(!isActive || Platform.OS !== "web" || typeof globalThis.addEventListener !== "function") {
            return;
        }

        let frame: number | null = null;

        const onScroll = () => {
            if(frame !== null) {
                return;
            }

            frame = requestAnimationFrame(() => {
                frame = null;

                measure();
            });
        };

        globalThis.addEventListener("scroll", onScroll, true);

        return () => {
            globalThis.removeEventListener("scroll", onScroll, true);

            if(frame !== null) {
                cancelAnimationFrame(frame);
            }
        };
    }, [
        isActive,
        measure
    ]);

    const onContentLayout = useCallback((event: LayoutChangeEvent) => {
        const {
            height,
            width
        } = event.nativeEvent.layout;

        setMeasuredContentSize((previousSize) => {
            if(previousSize && previousSize.height === height && previousSize.width === width) {
                return previousSize;
            }

            return {
                height,
                width
            };
        });
    }, []);

    const contentSize = contentSizeProp ?? measuredContentSize;

    const position = getAnchoredPosition({
        collisionPadding: collisionPaddingProp ?? spaces.spacingMd,
        boundaryRect: {
            height: windowHeight - insetTop - insetBottom,
            width: windowWidth - insetLeft - insetRight,
            x: insetLeft,
            y: insetTop
        },
        contentSize: contentSize ?? EMPTY_RECT,
        offset: offsetProp ?? spaces.spacingSm,
        anchorRect: anchorRect ?? EMPTY_RECT,
        direction,
        align,
        side
    });

    return {
        ...position,
        isPositioned: isActive && anchorRect !== null && contentSize !== null,
        onContentLayout,
        measure
    };
};
export default useAnchoredPosition;
